import { zodResolver } from '@hookform/resolvers/zod';
import { Box } from '@material-ui/core';
import { postCreateUser, randomPassword, UserType } from '@nemuru-eng/js-api-client';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';

import { FormGridContainer } from '@/components/shared/components/FormGridContainer/FormGridContainer';
import { SelectFormField } from '@/components/shared/components/NewContextForms/SelectFormField';
import { TextFormField } from '@/components/shared/components/NewContextForms/TextFormField';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import {
  AddFormUserSchema,
  AddFormUserType,
} from '@/components/shared/components/UserList/components/UserActionsTableDialog/schemas/AddFormUserSchema';
import { RoleMap } from '@/components/shared/components/UserList/maps/Role.map';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { EditDialogWrapper } from '@/components/shared/components/EditDialogWrapper';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useRequest } from '@/hooks/requests/useRequest';

const DEFAULT_PASSWORD = 'KDLrxWN25V@6G_Hw';

interface AddUserFormProps {
  userId: string;
  userType: UserType;
  opened: boolean;
  setOpened: (boolean) => void;
  revalidate: () => Promise<any>;
  handleClose: () => void;
}

export function AddUserForm({ userId, userType, opened, setOpened, handleClose, revalidate }: AddUserFormProps) {
  const { request, loading } = useRequest();
  const { getTextIdTranslation } = useTranslation();

  const role = RoleMap.fromUserType(userType);

  const onSubmitForm = async (formValues: AddFormUserType) => {
    let password = formValues.password;
    if (password === '' || !password) {
      password = DEFAULT_PASSWORD;
    }
    await request(postCreateUser, {
      id: uuidv4(),
      userId,
      userType: formValues.userType,
      roles: [formValues.userRole],
      userName: formValues.username,
      password,
      active: true,
    });
    revalidate();
    handleClose();
  };

  const userTypeOptionsArray = [
    {
      value: userType,
      display: <Text tId={userType} />,
    },
  ];

  const userRoleOptionsArray = [
    {
      value: role,
      display: <Text tId={role} />,
    },
  ];

  const formMethods = useForm<z.infer<ReturnType<typeof AddFormUserSchema>>>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(AddFormUserSchema(getTextIdTranslation)),
  });

  const generateStrongPassword = () =>
    formMethods.setValue('password', randomPassword({ length: 20, includeSpecialCharacters: false }));

  return (
    <FormProvider {...formMethods}>
      <EditDialogWrapper
        opened={opened}
        setOpened={setOpened}
        handleClose={handleClose}
        DialogProps={{
          maxWidth: 'sm',
          fullWidth: true,
        }}
        title={'ADD_USER'}
        onSubmitForm={onSubmitForm}
        saveButtonTextId={'ADD_USER'}
        isLoading={loading}
        formSchema={AddFormUserSchema}
        formMethodsProps={formMethods}
      >
        <Box pt={2}>
          <FormGridContainer fieldVariant={FieldVariant.form} fullWidth>
            <TextFormField label={'EMAIL'} name={'username'} />
            <TextFormField label={'PASSWORD_OPTIONAL'} name={'password'} />
            <SelectFormField name={'userType'} label={'USER_TYPE'} selectOptions={userTypeOptionsArray} />
            <SelectFormField name={'userRole'} label={'USER_ROLE'} selectOptions={userRoleOptionsArray} />
          </FormGridContainer>
          <Box mt={2} display={'flex'} justifyContent={'flex-end'}>
            <StandardButton
              color={'default'}
              size={'small'}
              variant={'text'}
              onClick={generateStrongPassword}
              startIcon={<i className={'material-icons-outlined'}>shuffle</i>}
            >
              <Text tId={'GENERATE_RANDOM_PASSWORD'} />
            </StandardButton>
          </Box>
        </Box>
      </EditDialogWrapper>
    </FormProvider>
  );
}
